import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Minus, Plus, ShoppingBag, LoaderCircle, PartyPopper, CreditCard, HandCoins } from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { t, fmtMoney } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const loadRazorpay = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load payment checkout"));
    document.body.appendChild(s);
  });

export const CartDrawer = () => {
  const { list, total, setQty, clear, open, setOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [block, setBlock] = useState("");
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [payConfig, setPayConfig] = useState(null);

  const loc = user?.localization || {};
  const lang = loc.language || localStorage.getItem("funz_lang") || "en";
  const currency = loc.currency || "INR";
  const symbol = loc.symbol || "₹";
  const money = (v) => fmtMoney(v, currency, symbol);

  useEffect(() => {
    if (!open || !user) return;
    const country = encodeURIComponent(user.localization?.country || "India");
    axios.get(`${API}/payments/config?country=${country}`).then((r) => setPayConfig(r.data)).catch(() => setPayConfig({ providers: [], cod: true }));
  }, [open, user]);

  if (!open) return null;

  const requireAuth = () => {
    if (!user) {
      setOpen(false);
      navigate("/login");
      return false;
    }
    return true;
  };

  const orderKinds = new Set(list.map((i) => i.kind || "food"));
  const singleKind = orderKinds.size === 1;
  const providers = (payConfig?.providers || []).filter((p) => p.enabled);
  const onlineEnabled = providers.length > 0 && singleKind;

  const checkoutCOD = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const food = list.filter((i) => i.kind !== "shop");
      const shop = list.filter((i) => i.kind === "shop");
      let foodRes = null;
      let shopRes = null;
      if (food.length) {
        const { data } = await axios.post(
          `${API}/orders`,
          { items: food.map((i) => ({ name: i.name, price: i.price, qty: i.qty })), phone, block },
          authHeaders()
        );
        foodRes = data;
      }
      if (shop.length) {
        const { data } = await axios.post(
          `${API}/shop/orders`,
          { items: shop.map((i) => ({ product_id: i.product_id, qty: i.qty })), phone, address: block },
          authHeaders()
        );
        shopRes = data;
      }
      setPlaced({
        order_id: (foodRes || shopRes).order_id,
        total: (foodRes?.total || 0) + (shopRes?.total || 0),
        eta: foodRes ? "18–25 min" : "2–4 days",
        method: t(lang, "cod_method"),
        split: foodRes && shopRes,
      });
      clear();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const checkoutRazorpay = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const isShop = list[0]?.kind === "shop";
      const items = isShop
        ? list.map((i) => ({ name: i.name, price: i.price, qty: i.qty, product_id: i.product_id }))
        : list.map((i) => ({ name: i.name, price: i.price, qty: i.qty }));
      const { data } = await axios.post(`${API}/payments/order`, { items, phone, block, kind: isShop ? "shop" : "food" }, authHeaders());
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "FUNZ FoodBite",
        description: list.map((i) => `${i.qty}× ${i.name}`).join(", ").slice(0, 120),
        order_id: data.razorpay_order_id,
        prefill: { email: user.email, contact: phone },
        theme: { color: "#7C3AED" },
        modal: {
          ondismiss: () => toast.error("Payment cancelled — your order wasn't placed."),
        },
        handler: async (resp) => {
          try {
            await axios.post(
              `${API}/payments/verify`,
              {
                order_id: data.order_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
              authHeaders()
            );
            setPlaced({ order_id: data.order_id, total: data.amount / 100, eta: "18–25 min", method: t(lang, "paid_online") });
            clear();
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed. Contact support.");
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const checkoutStripe = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const isShop = list[0]?.kind === "shop";
      const items = isShop
        ? list.map((i) => ({ name: i.name, price: i.price, qty: i.qty, product_id: i.product_id }))
        : list.map((i) => ({ name: i.name, price: i.price, qty: i.qty }));
      const { data } = await axios.post(
        `${API}/payments/stripe/checkout`,
        { items, phone, block, kind: isShop ? "shop" : "food", origin_url: window.location.origin },
        authHeaders()
      );
      clear();
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-white/10 bg-[#09090B] p-6 rtl:left-0 rtl:right-auto rtl:border-l-0 rtl:border-r">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2.5 font-display text-xl font-extrabold text-white">
            <ShoppingBag className="h-5 w-5 text-[#06B6D4]" /> {t(lang, "cart_title")}
          </h3>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="rounded-full border border-white/10 p-2 text-slate-400 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        {placed ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center" data-testid="order-success">
            <PartyPopper className="h-12 w-12 text-[#06B6D4]" />
            <p className="mt-4 font-display text-2xl font-extrabold text-white">{t(lang, "order_placed")}</p>
            <p className="mt-2 text-sm text-slate-400">
              <span className="font-jbmono text-[#06B6D4]">#{placed.order_id.slice(-6)}</span> · {money(placed.total)} · ETA {placed.eta}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {placed.method}{placed.split ? ` · ${t(lang, "split_note")}` : ""} · {t(lang, "track_in_wallet")}
            </p>
            <button
              data-testid="order-done"
              onClick={() => {
                setPlaced(null);
                setOpen(false);
              }}
              className="mt-6 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-8 py-3 font-display text-xs font-bold uppercase tracking-wider text-white"
            >
              {t(lang, "done")}
            </button>
          </div>
        ) : list.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <span className="text-5xl">🍔</span>
            <p className="mt-4 font-display text-lg font-bold text-white">{t(lang, "cart_empty")}</p>
            <p className="mt-1 text-sm text-slate-500">{t(lang, "cart_empty_sub")}</p>
          </div>
        ) : (
          <>
            <div className="mt-6 flex-1 space-y-3 overflow-y-auto pr-1 rtl:pl-1 rtl:pr-0">
              {list.map((i) => (
                <div key={i.name} data-testid={`cart-item-${i.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-3.5">
                  <span className="text-2xl">{i.emoji}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-white">{i.name}</p>
                    <p className="font-jbmono text-[11px] text-[#06B6D4]">{money(i.price)} × {i.qty}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`cart-minus-${i.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setQty(i.name, i.qty - 1)} className="rounded-full border border-white/10 p-1.5 text-slate-300 hover:text-white">
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="w-5 text-center font-jbmono text-sm text-white">{i.qty}</span>
                    <button data-testid={`cart-plus-${i.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setQty(i.name, i.qty + 1)} className="rounded-full border border-white/10 p-1.5 text-slate-300 hover:text-white">
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 pt-4">
              <div className="flex justify-between font-display text-lg font-extrabold text-white">
                <span>{t(lang, "cart_total")}</span>
                <span data-testid="cart-total">{money(total)}</span>
              </div>
              {currency !== "INR" && (
                <p className="mt-1 text-right font-jbmono text-[10px] text-slate-500 rtl:text-left" data-testid="cart-total-inr">
                  ≈ ₹{total} INR
                </p>
              )}
              <div className="mt-4 space-y-3">
                <input
                  data-testid="checkout-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t(lang, "phone_ph")}
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#7C3AED]/60"
                />
                <input
                  data-testid="checkout-block"
                  value={block}
                  onChange={(e) => setBlock(e.target.value)}
                  placeholder={t(lang, "address_ph")}
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#7C3AED]/60"
                />
                {onlineEnabled &&
                  providers.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`checkout-${p.id}`}
                      onClick={p.id === "stripe" ? checkoutStripe : checkoutRazorpay}
                      disabled={busy || !phone.trim() || !block.trim()}
                      className="glow-btn-b flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] py-4 font-display text-sm font-bold uppercase tracking-wider text-white disabled:opacity-50"
                    >
                      {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                      {user ? `${t(lang, "pay_online")} · ${money(total)} · ${p.label}` : t(lang, "login_order")}
                    </button>
                  ))}
                <button
                  data-testid="checkout-submit"
                  onClick={checkoutCOD}
                  disabled={busy || !phone.trim() || !block.trim()}
                  className={`flex w-full items-center justify-center gap-2 rounded-full py-4 font-display text-sm font-bold uppercase tracking-wider disabled:opacity-50 ${
                    onlineEnabled
                      ? "border border-[#F97316]/40 text-[#F97316] transition-colors hover:bg-[#F97316]/10"
                      : "glow-btn-b bg-gradient-to-r from-[#F97316] to-[#22C55E] text-[#09090B]"
                  }`}
                >
                  {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <HandCoins className="h-4 w-4" />}
                  {user ? `${t(lang, "pay_delivery")} · ${money(total)}` : t(lang, "login_order")}
                </button>
                <p className="text-center font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
                  {onlineEnabled
                    ? `${providers.map((p) => p.label).join(" · ")} · ${t(lang, "pay_note_online")}`
                    : t(lang, "pay_note_offline")}
                </p>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};
